<div class="pt-4 pb-12 px-6 md:px-12 lg:px-27">
    <header>
        <h1 class="h3-text text-text-primary my-2 text-center" i18n>Frequently Asked Questions</h1>
    </header>

    <mat-accordion >
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <span class="h4-text text-text-primary" i18n>General Questions</span>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>What is Horse Hop?</h4>
                    <p class="body-s-text" i18n>Horse Hop is a platform designed to connect horse owners who need transportation for their horses with professional hauliers or amateur drivers willing to share space in their vehicles. It helps save on transport costs by sharing trip expenses.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How does Horse Hop work?</h4>
                    <p class="body-s-text" i18n>You create an account, either post a ride or search for available ones, connect with drivers or other horse owners, agree on the terms, and transport your horses together.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Who can use Horse Hop?</h4>
                    <p class="body-s-text" i18n>Horse Hop is suitable for private horse owners looking for cost-effective transportation options, amateur drivers who transport horses for short distance, as well as professional hauliers seeking ways to reduce empty leg trips.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Is Horse Hop available in all regions?</h4>
                    <p class="body-s-text" i18n>Currently, Horse Hop is focused on Europe, but we welcome users from other regions as well. Check the available locations when posting or searching for trips.</p>
                </div>
            </section>
        </mat-expansion-panel>
    
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <span class="h4-text text-text-primary" i18n>Registration and Account</span>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>How do I register on Horse Hop?</h4>
                    <p class="body-s-text" i18n>Click on the <a routerLink="/register" class="underline text-blue-600">"Sign up"</a> button, fill in your personal details, verify your email address and your account will be ready to use.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Do I need an account to use Horse Hop?</h4>
                    <p class="body-s-text" i18n>Yes, registration is required to post trips, send booking requests, and communicate with other users.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>What should I do if I forgot my password?</h4>
                    <p class="body-s-text" i18n>Click on the <a routerLink="/profile/password/forgot" class="underline text-blue-600">"Forgot Password?"</a> link on the login page and follow the instructions to reset your password.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Can I update my profile information?</h4>
                    <p class="body-s-text" i18n>Yes, you can update your profile details, such as contact information and preferences, in the <a routerLink="/profile" class="underline text-blue-600">"Profile"</a> section of your account. To update your personal information, such as first or last name, please contact us at <a href="mailto:info@horsehop.eu" class="underline text-blue-600">info&#64;horsehop.eu</a> or use the <a routerLink="/feedback" class="underline text-blue-600">"Contact Us"</a> button on the site.</p>
                </div>
            </section>
        </mat-expansion-panel>
    
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <span class="h4-text text-text-primary" i18n>Rides</span>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>How do I post a ride?</h4>
                    <p class="body-s-text" i18n>Click on <a routerLink="/ride/create" class="underline text-blue-600">"New ride"</a> button in the upper right corner. Fill in the details, including the starting point, destination, date, and any specific conditions or preferences. Once submitted, your ride will be visible to potential users.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How can I search for rides for my horse?</h4>
                    <p class="body-s-text" i18n>Use the homepage search tool. Enter the departure and destination points and desired dates to find matching trips. Browse the results, select a suitable option, and submit a booking request.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Can I edit or cancel a ride I’ve posted?</h4>
                    <p class="body-s-text" i18n>Yes, you can edit or cancel your ride anytime in the <a routerLink="/my-rides" class="underline text-blue-600">"My Rides"</a> section. If you cancel, all participants will be automatically notified.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>What occurs if a driver or participant cancels a ride?</h4>
                    <p class="body-s-text" i18n>You will receive a notification email. You can then look for alternative rides or repost your request.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Is it possible to track rides in real time?</h4>
                    <p class="body-s-text" i18n>Currently, real-time tracking is not available. However, you can communicate directly with the driver to stay updated on the status of the ride.</p>
                </div>
            </section>
        </mat-expansion-panel>

        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <span class="h4-text text-text-primary" i18n>Ride Requests</span>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>How does the Ride Request and Ride Offer process work?</h4>
                    <ol class="body-s-text list-decimal pl-5">
                        <li i18n>You publish a Ride Request with your requirements (date, location, horse details, and any extras).</li>
                        <li i18n>Verified drivers submit Ride Offers with their price and schedule.</li>
                        <li i18n>You review and accept the best offer, paying the booking fee (Service Fee) to Horse Hop.</li>
                        <li i18n>You and the driver arrange trip details in chat on the platform.</li>
                        <li i18n>After your horse is delivered, you pay the agreed price (Contribution) directly to the driver.</li>
                    </ol>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How do I submit a Ride Request?</h4>
                    <p class="body-s-text" i18n>You can create a Ride Request by clicking on the <a routerLink="/new-ride" class="underline text-blue-600">"New ride"</a> button in the upper right corner. Enter the necessary details, including the location, date, and number of horses needing transport.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How do I choose the best Ride Offer?</h4>
                    <p class="body-s-text" i18n>You can review available Ride Offers and check driver reviews to decide based on your personal criteria, for example such as pricing, schedule, and driver experience.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Can I negotiate with a driver after accepting an offer?</h4>
                    <p class="body-s-text" i18n>Yes! Once you accept the best available offer, you and the driver have 3 days to negotiate. If you don't revoke your acceptance within this period, the agreement is considered final, and a fee is charged. If you decide the offer is unsuitable during negotiation, the service fee is refunded, allowing you to choose another Ride Offer.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How are payments handled?</h4>
                    <p class="body-s-text" i18n>Payments are made directly to the driver — our platform is not involved in processing transactions.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Can I cancel my Ride Request?</h4>
                    <ul class="body-s-text list-disc pl-5">
                        <li i18n>Before acceptance: Free cancellation if no offers were made or accepted.</li>
                        <li i18n>During negotiation: Free cancellation within the 3-day negotiation period.</li>
                        <li i18n>After negotiation period: Cancellation is possible, but the request fee is non-refundable.</li>
                    </ul>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Can I specify additional requirements in my request?</h4>
                    <p class="body-s-text" i18n>Yes! You can include extra details when you publish your Ride Request to ensure the journey meets your needs. For example, if you need to take your equipment for the show or bring your pet along with your horses, let the drivers know about that by ticking the respective checkbox</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>What is the difference between a Shared and Private Ride Request?</h4>
                    <p class="body-s-text" i18n>A Shared Ride Request allows the driver to pick up other horses during the trip, while a Private Ride Request is exclusively for your horses.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Do drivers need to be verified before submitting a Ride Offer?</h4>
                    <p class="body-s-text" i18n>Yes, all drivers must complete driving licence and face verification before they can submit a Ride Offer.</p>
                </div>
            </section>
        </mat-expansion-panel>

        <mat-expansion-panel>
            <mat-expansion-panel-header>
            <mat-panel-title>
                <span class="h4-text text-text-primary" i18n>Ride Offers</span>
            </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>What is a Ride Offer?</h4>
                    <p class="body-s-text" i18n>A Ride Offer is a proposal from a driver to horse owner submitted in response to Ride Request</p>
                </div>     
                <div>
                    <h4 class="h4-text my-2" i18n>Where can I find available Ride Requests?</h4>
                    <p class="body-s-text" i18n>You can browse outstanding Ride Requests on the <a routerLink="/ride-request/search" class="underline text-blue-600">'Search Ride Requests'</a> page.</p>
                </div>           
                <div>
                    <h4 class="h4-text my-2" i18n>How do I create a Ride Offer?</h4>
                    <p class="body-s-text" i18n>To create a Ride Offer, find a Ride Request, open its details and click button "Create ride offer"</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>What details can I include in my Ride Offer?</h4>
                    <p class="body-s-text" i18n>Drivers can specify price and departure/arrival dates if allowed by conditions of the Ride Request.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Are there legal requirements I need to follow?</h4>
                    <p class="body-s-text" i18n>All drivers must comply with the EC 1/2005 directive regarding horse welfare during transportation.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How are payments handled?</h4>
                    <p class="body-s-text" i18n>Drivers must be paid directly by horse owners. The agreement is legally binding, and the horse owner is obliged to pay once the horse is successfully delivered.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Can I cancel a Ride Offer after it's accepted?</h4>
                    <ul class="body-s-text list-disc pl-5">
                        <li i18n>Within 3 days of acceptance: You can cancel only if you and the horse owner mutually agree that the offer is unsuitable.</li>
                        <li i18n>After 3 days: The Ride Offer cannot be canceled.</li>
                        <li i18n>Failure to provide transport services: If a driver does not arrive on agreed time or fails to fulfill the transport agreement, we reserve the right to fine the driver according to our <a routerLink="/legal/terms-of-service" class="underline text-blue-600">General Terms & Conditions</a>.</li>
                    </ul>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How do I communicate with horse owners?</h4>
                    <p class="body-s-text" i18n>You can discuss all ride details using the Horse Hop chat.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>What are the vehicle requirements?</h4>
                    <p class="body-s-text" i18n>Your vehicle must be roadworthy, and you must possess all necessary documents as required by our <a routerLink="/legal/terms-of-service" class="underline text-blue-600">General Terms & Conditions</a>.</p>
                </div>
            </section>
        </mat-expansion-panel>
        
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <span class="h4-text text-text-primary" i18n>Payments</span>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>What to I need to pay to transport my horse?</h4>
                    <p class="body-s-text" i18n>There are two payments: <strong>Service Fee</strong> which you pay to Horse Hop for using the platform, and <strong>Contribution</strong> which you pay directly to the driver to cover transportation expenses.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How much is Service Fee for using Horse Hop?</h4>
                    <p class="body-s-text" i18n>Horse Hop charges a 10% fee proportional to the amount of your contribution, but no less than 5 EUR (or equivalent in currency of your contribution). For example, if your contribution is 100 EUR, Horse Hop will charge 10 EUR. If your contribution is 30 EUR, Horse Hop will charge 5 EUR. The Service Fee is for intermediary services only and is separate from the transport cost.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How Contribution amount for booking is calculated?</h4>
                    <p class="body-s-text" i18n>The suggested Contribution amount is calculated automatically based on the distance of your ride and driver's expenses. Drivers can provide details about the cost breakdown in their ride listings. Drivers can suggest a different amount of Contribution, if the calculated amount does not cover ride expenses. The Contribution is paid directly to the driver.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How and when Service Fee is charged?</h4>
                    <p class="body-s-text" i18n>We preauthorise the Service Fee amount from your credit card when you submit a booking request. Once booking request is accepted by the driver, we capture the preauthorised amount. The driver has 5 days to accept or reject your booking. If your booking was rejected by the driver, we cancel the preauthorisation. If you cancel your booking, Service Fee is charged according to Cancellation Policy. If a ride is not completed due to the driver's fault, the Service Fee may be refunded. The Contribution is handled directly between you and the driver.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How do I pay Contribution?</h4>
                    <p class="body-s-text" i18n>Contribution payments are arranged directly between the horse owner and the driver. Horse Hop does not process payments but provides a platform for connecting users.</p>
                </div>                
                <div>
                    <h4 class="h4-text my-2" i18n>What happens if there’s a dispute about payments?</h4>
                    <p class="body-s-text" i18n>Users are encouraged to resolve disputes directly. If you cannot resolve a dispute directly, you may contact Horse Hop for mediation. If both parties agree, Horse Hop’s decision is binding. Otherwise, you may still seek resolution through other means, including legal action. For more details, see our <a routerLink="/legal/terms-of-service" class="underline text-blue-600">General Terms & Conditions</a>.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>What evidence is considered in case of a dispute?</h4>
                    <p class="body-s-text" i18n>Only the content of the chat within the Horse Hop platform is considered reliable evidence of any negotiations or agreements during disputes. Content from other means of communication (such as other messengers, emails, or recorded calls) may be evaluated, but is not considered reliable evidence.</p>
                </div>
                
            </section>
        </mat-expansion-panel>
    
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <span class="h4-text text-text-primary" i18n>Safety and Security</span>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>How do I know my horse will be safe during transport?</h4>
                    <p class="body-s-text" i18n>All drivers on Horse Hop are required to verify their identity and driving licence before posting Rides or Ride Offers. You can also review their profiles, ratings, and reviews before booking. Feel free to communicate with drivers to ensure your horse’s safety requirements are met.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>What should I do if I encounter unprofessional behaviour?</h4>
                    <p class="body-s-text" i18n>If you experience any issues, please report them to Horse Hop’s support team. Include details about the situation, and we will investigate promptly.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Are there any insurance options for my horse during transport?</h4>
                    <p class="body-s-text" i18n>Horse Hop does not provide insurance directly at the moment. However, you can inquire with the driver or check your existing insurance for coverage during transport.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How do I report abuse, fraud, or suspicious activity?</h4>
                    <p class="body-s-text" i18n>If you encounter any abuse, fraud, or suspicious activity, please contact Horse Hop support immediately using <a routerLink="/feedback" class="underline text-blue-600">"Contact Us" page</a>. In case of criminal actions, such as theft of a horse or vehicle, you should also immediately contact the police.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>How is my data protected?</h4>
                    <p class="body-s-text" i18n>Your data is protected and processed in accordance with our <a routerLink="/legal/privacy-policy" class="underline text-blue-600">Privacy Policy</a>.</p>
                </div>
            </section>
        </mat-expansion-panel>
    
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <span class="h4-text text-text-primary" i18n>Communication</span>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>How can I communicate with other users?</h4>
                    <p class="body-s-text" i18n>Once you find a ride or participant, you can use the built-in messaging system on Horse Hop to communicate and agree on details.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Can I rate and review drivers or horse owners?</h4>
                    <p class="body-s-text" i18n>Yes, after completing the ride, both parties can leave ratings and reviews. This helps build trust and reliability within the community.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>What if I have additional questions?</h4>
                    <p class="body-s-text" i18n>For any questions not covered in the FAQ, you can contact our support team via the <a routerLink="/feedback" class="underline text-blue-600">"Contact Us"</a> section on the website.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>When can I expect a response from support?</h4>
                    <p class="body-s-text" i18n>While Horse Hop currently cannot commit to specific support hours or response times, we aim to assist you as soon as possible.</p>
                </div>
            </section>
        </mat-expansion-panel>
    
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>
                    <span class="h4-text text-text-primary" i18n>Technical Issues</span>
                </mat-panel-title>
            </mat-expansion-panel-header>
            <section class="text-text-primary">
                <div>
                    <h4 class="h4-text my-2" i18n>What should I do if I encounter a technical issue?</h4>
                    <p class="body-s-text" i18n>If you experience a problem with the website or app, please report it to <a href="mailto:info@horsehop.eu" class="underline text-blue-600">info&#64;horsehop.eu</a> or use the <a routerLink="/feedback" class="underline text-blue-600">"Contact Us"</a> button on the site. Please include as many details as possible.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Which devices and browsers are supported?</h4>
                    <p class="body-s-text" i18n>Horse Hop is compatible with most modern browsers (e.g., Chrome, Firefox, Safari) and works on both desktop and mobile devices.</p>
                </div>
                <div>
                    <h4 class="h4-text my-2" i18n>Can I delete my account?</h4>
                    <p class="body-s-text" i18n>Yes, you can request account deletion through the <a routerLink="/profile" class="underline text-blue-600">"Profile"</a> section or by contacting our support team.</p>
                </div>
            </section>
        </mat-expansion-panel>
    </mat-accordion>
</div>